import { useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

const t = (s) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;

// Review & fix one step's auto-captions (timings stay; only words change). Empty a line to drop it.
export default function CaptionEditModal({ manualId, index, onClose }) {
  const open = index != null;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [on, setOn] = useState(true);
  const [edited, setEdited] = useState(false);
  const base = `/manuals/${manualId}/steps/${index}/captions`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(base);
      setChunks(data.chunks || []); setOn(data.captions !== false); setEdited(!!data.edited);
      if (!data.has_speech) toast.message("No recorded voice or clip sound on this step yet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load captions"); onClose(); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); else setChunks([]); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const kept = chunks.filter((c) => c.text.trim());
      await api.patch(base, kept.length ? { chunks: kept, captions: on } : { captions: chunks.length ? false : on });
      toast.success("Captions saved — re-render to see them");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save captions"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try { await api.delete(`${base}/edits`); await load(); toast.success("Back to the original transcript"); }
    catch { toast.error("Couldn't reset"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="bg-[#0f0f16] border-2 border-[#5d8a52] text-white max-w-md max-h-[88vh] overflow-y-auto" data-testid="caption-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white text-lg font-extrabold">
            <span className="px-1 rounded-[3px] text-[10px] leading-tight border border-white">CC</span> Step {index != null ? index + 1 : ""} captions
          </DialogTitle>
          <DialogDescription className="text-white/70">Fix any misheard word. Timing stays matched to your voice — clear a line to remove it.</DialogDescription>
        </DialogHeader>
        <button type="button" onClick={() => setOn((v) => !v)} data-testid="caption-edit-toggle"
          className={`self-start inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-extrabold border-2 transition-colors ${on ? "border-white bg-white text-black" : "border-white/25 text-white/70"}`}>
          Auto Captions {on ? "on" : "off"}
        </button>
        {loading ? (
          <p className="flex items-center gap-2 text-sm font-bold text-[#f0c975] py-6 justify-center" data-testid="caption-edit-loading"><Loader2 size={16} className="spin" /> Listening to your step…</p>
        ) : (
          <div className={`space-y-2 ${on ? "" : "opacity-40 pointer-events-none"}`} data-testid="caption-edit-list">
            {chunks.length === 0 && <p className="text-sm text-white/60 text-center py-3">No spoken words found on this step.</p>}
            {chunks.map((c, k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="shrink-0 w-12 text-[10px] font-bold text-white/45 tabular-nums">{t(c.a)}</span>
                <input value={c.text} maxLength={80} data-testid={`caption-edit-chunk-${k}`}
                  onChange={(e) => setChunks((arr) => arr.map((x, j) => (j === k ? { ...x, text: e.target.value } : x)))}
                  className="flex-1 min-w-0 px-3 py-2 text-sm font-bold rounded-xl bg-black text-white border-2 border-[#5d8a52] focus:border-[#a9d79b] focus:outline-none" />
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-1">
          {edited && (
            <button type="button" onClick={reset} disabled={saving || loading} data-testid="caption-edit-reset"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-white/25 px-4 py-2.5 text-sm font-extrabold hover:bg-white/10 disabled:opacity-40 transition-colors">
              <RotateCcw size={14} /> Original
            </button>
          )}
          <button type="button" onClick={save} disabled={saving || loading} data-testid="caption-edit-save"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#dc2626] hover:bg-[#b91c1c] py-2.5 font-extrabold disabled:opacity-50 transition-colors">
            {saving && <Loader2 size={15} className="spin" />} Save captions
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
